import type { IProject } from '@pulse/shared';
import { Chip, LinearProgress, Stack, Typography } from '@pulse/ui';

import { useIssues } from '../hooks/useIssues';
import { completionPercent, expectedCompletionPercent, scheduleStatus, SCHEDULE_STATUS_CONFIG } from '../utils/projectCompletion';

// Compact row/card rendering of the same completion + schedule-status logic as
// ProjectCompletionCard.tsx (the Project Overview tab's version) — sized for a table cell or
// mobile card rather than a standalone Paper section. One `useIssues` call per visible row;
// the list page already paginates (25 rows/page by default), which bounds this the same way
// ProjectMetricsPanel's per-project client-side aggregation is already accepted elsewhere.
export function ProjectCompletionMini({ project }: { project: IProject }) {
  const { data: issues = [] } = useIssues(project.id);
  return <ProjectCompletionBar project={project} percent={completionPercent(issues)} issueCount={issues.length} />;
}

// The display half of ProjectCompletionMini, for a caller that already has the figures — the
// Home page's "My Projects" card gets every project's completion from one request instead of
// fetching each project's issues. Only startDate/targetDate are read off `project`, for the
// schedule status.
export function ProjectCompletionBar({
  project,
  percent,
  issueCount,
}: {
  project: Pick<IProject, 'startDate' | 'targetDate'>;
  percent: number;
  issueCount: number;
}) {
  const total = issueCount;
  const today = new Date().toISOString().slice(0, 10);
  const expected = project.targetDate ? expectedCompletionPercent(project.startDate, project.targetDate, today) : null;
  const status = project.targetDate && expected !== null ? scheduleStatus(percent, expected, project.targetDate, today) : null;

  if (total === 0) {
    return <Typography variant="caption" color="text.secondary">No issues</Typography>;
  }

  return (
    <Stack spacing={0.5} sx={{ minWidth: 110 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" fontWeight={600}>{percent}%</Typography>
        {status && (
          <Chip
            size="small"
            label={SCHEDULE_STATUS_CONFIG[status].label}
            color={SCHEDULE_STATUS_CONFIG[status].color}
            sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
          />
        )}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={status === 'atRisk' ? 'warning' : status === 'overdue' ? 'error' : percent === 100 ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Stack>
  );
}
